<template>
    <Form v-slot="{ invalid }">
        <v-dialog v-model="visible" width="500">
            <template #activator="{}">
                <v-btn class="px-3 mr-2 fit-content" color="primary" @click="open">
                    {{ $t('einstellungen.benutzerliste.button') }}
                </v-btn>
            </template>
            <FocusLock>
                <form id="focusLockDialogForm" @submit.prevent="submit">
                    <v-card>
                        <v-card-title class="headline font-weight-bold mb-2">
                            {{ $t('lieferant.einstellungen.adduserdialog.title') }}
                        </v-card-title>

                        <modal-header-notification></modal-header-notification>

                        <v-card-text class="pb-0 pt-0">
                            <Field v-slot="{ errors, field }" v-model="benutzer.email" name="Email" :rules="getValidation('benutzerCreateApiDto.email')">
                                <v-text-field
                                    v-bind="field"
                                    v-model="benutzer.email"
                                    autofocus
                                    :label="$t('einstellungen.addaccount.email') + '*'"
                                    placeholder="example@test.com"
                                    :error-messages="errors"
                                ></v-text-field>
                            </Field>
                            <Field
                                v-slot="{ errors, field }"
                                v-model="benutzer.passwort"
                                name="Passwort"
                                :rules="getValidation('benutzerCreateApiDto.passwort') + '|valid_password_format'"
                            >
                                <v-text-field
                                    v-bind="field"
                                    v-model="benutzer.passwort"
                                    :label="$t('einstellungen.addaccount.password') + '*'"
                                    type="password"
                                    :error-messages="errors"
                                ></v-text-field>
                            </Field>
                            <Field v-slot="{ errors, field }" v-model="benutzer.vorname" name="Vorname" :rules="getValidation('benutzerCreateApiDto.vorname')">
                                <v-text-field
                                    v-bind="field"
                                    v-model="benutzer.vorname"
                                    :label="$t('einstellungen.addaccount.vorname') + '*'"
                                    placeholder="..."
                                    :error-messages="errors"
                                ></v-text-field>
                            </Field>
                            <Field
                                v-slot="{ errors, field }"
                                v-model="benutzer.nachname"
                                name="Nachname"
                                :rules="getValidation('benutzerCreateApiDto.nachname')"
                            >
                                <v-text-field
                                    v-bind="field"
                                    v-model="benutzer.nachname"
                                    :label="$t('einstellungen.addaccount.nachname') + '*'"
                                    placeholder="..."
                                    :error-messages="errors"
                                ></v-text-field>
                            </Field>
                            <Field v-slot="{ errors, field }" v-model="benutzer.rolle" name="Rolle" rules="required">
                                <v-select
                                    v-bind="field"
                                    v-model="benutzer.rolle"
                                    :error-messages="errors"
                                    :item-props="itemProps"
                                    :items="lieferantBenutzerRollen"
                                    :label="$t('einstellungen.addaccount.rolle') + '*'"
                                    :menu-props="{ attach: '#focusLockDialogForm' }"
                                />
                            </Field>
                            <Field
                                v-slot="{ errors, field }"
                                v-model="benutzer.mobilNummer"
                                name="MobilNummer"
                                :rules="getValidation('benutzerCreateApiDto.mobilNummer') + '|valid_swiss_telephone_number'"
                            >
                                <v-text-field
                                    v-bind="field"
                                    v-model="benutzer.mobilNummer"
                                    :label="$t('einstellungen.addaccount.mobilnummer') + '*'"
                                    placeholder="+41 77 111 22 33"
                                    :error-messages="errors"
                                ></v-text-field>
                            </Field>
                            <Field v-slot="{ field, errors }" v-model="benutzer.sprache" name="Sprache" :rules="'required'">
                                <v-select
                                    :items="sprachen"
                                    :label="$t('einstellungen.addaccount.sprache') + '*'"
                                    :error-messages="errors"
                                    :menu-props="{ attach: '#focusLockDialogForm' }"
                                    :model-value="field.value"
                                    @update:model-value="field.onChange"
                                    @blur="field.onBlur"
                                ></v-select>
                            </Field>
                        </v-card-text>

                        <v-card-actions class="ml-4">
                            <v-btn color="primary" :disabled="invalid || requiredFieldsFilled" variant="flat" type="submit" class="modalbutton mb-3">
                                {{ $t('components.dialog.save') }}
                            </v-btn>
                            <v-btn class="modalbutton mb-3" variant="flat" @click="visible = false">
                                {{ $t('components.dialog.cancel') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </form>
            </FocusLock>
        </v-dialog>
    </Form>
</template>

<script lang="ts">
import { BenutzerCreateApiDto } from '@/api-schema';
import { Component, Vue } from 'vue-facing-decorator';

import { namespace } from 'vuex-facing-decorator';

import { NAME as NAME_EINSTELLUNGEN } from '../store';
const profilModul = namespace(NAME_EINSTELLUNGEN);

import { NAME as NAME_VALIDATION } from '@/store/modules/validation';
const validationModul = namespace(NAME_VALIDATION);

import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
const headerNotificationModul = namespace(NAME_HEADERNOTIFICATION);

import { ADD_ACCOUNT } from '../store/actions';
import { GET_VALIDATION_KEY } from '@/store/modules/validation/getters';

import { Form, Field } from 'vee-validate';
import modalHeaderNotification from '@/components/ModalHeaderNotification.vue';
import { CLOSE_MODAL } from '@/store/modules/headerNotification/actions';
@Component({
    components: {
        Form,
        Field,
        modalHeaderNotification
    }
})
export default class LieferantAddAccountDialog extends Vue {
    private visible = false;
    private benutzer: BenutzerCreateApiDto = {
        email: '',
        mobilNummer: '',
        nachname: '',
        vorname: '',
        passwort: '',
        sprache: ''
    };
    get lieferantBenutzerRollen(): Array<object> {
        return [
            {
                title: this.$t('benutzerrollen.lieferant.user'),
                beschreibung: this.$t('benutzerrollen.lieferant.user.helptext'),
                value: 'LieferantUser'
            },
            {
                title: this.$t('benutzerrollen.lieferant.main'),
                beschreibung: this.$t('benutzerrollen.lieferant.main.helptext'),
                value: 'LieferantMain'
            }
        ];
    }
    @profilModul.Action(ADD_ACCOUNT) add!: Function;
    @validationModul.Getter(GET_VALIDATION_KEY) getValidation!: (key: string) => string;
    @headerNotificationModul.Action(CLOSE_MODAL) closeHeader!: Function;

    private onSave() {
        this.add(this.benutzer).then((response) => {
            if (response) {
                this.visible = false;
            }
        });
    }

    private open() {
        console.log('opening');
        this.closeHeader();
        this.benutzer = Object.assign(
            {},
            {
                email: '',
                passwort: '',
                nachname: '',
                vorname: '',
                rolle: '',
                mobilNummer: ''
            }
        );
        console.log('visibling');
        this.visible = true;
    }

    submit() {
        this.onSave();
    }

    get sprachen(): Array<object> {
        return [
            { value: 'D', title: 'Deutsch' },
            { value: 'F', title: 'Français' },
            { value: 'I', title: 'Italiano' }
        ];
    }

    private itemProps(item) {
        return {
            title: item.title,
            subtitle: item.beschreibung
        };
    }

    get requiredFieldsFilled(): boolean {
        return (
            this.benutzer.email.length === 0 ||
            this.benutzer.passwort.length === 0 ||
            this.benutzer.nachname.length === 0 ||
            this.benutzer.vorname.length === 0 ||
            this.benutzer.mobilNummer.length === 0 ||
            !this.benutzer.rolle ||
            !this.benutzer.sprache
        );
    }
}
</script>
